import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage, centsToUsd } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ShoppingCart, Loader2 } from "lucide-react";

export const CartPanel = ({ orders, config, membership, onCheckedOut }) => {
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const demoMode = config?.demoMode;
  const reportPriceCents = config?.reportPriceCents ?? 25000;
  const tier = membership?.status === "active" ? config?.membershipTiers?.[membership.tier] : null;
  const maxPerCheckout = tier?.max_reports_per_checkout ?? config?.maxReportsPerCheckout ?? 25;

  const eligible = useMemo(
    () =>
      orders.filter(
        (order) =>
          order.purchase_type !== "single" &&
          order.verification_status === "customer_verified" &&
          Boolean(order.customer_verified_at) &&
          !order.report_paid
      ),
    [orders]
  );

  const toggle = (orderId) =>
    setSelected((previous) => {
      if (previous.includes(orderId)) return previous.filter((id) => id !== orderId);
      if (previous.length >= maxPerCheckout) {
        toast.warning(
          `Your ${tier?.name || "current"} membership allows up to ${maxPerCheckout} reports per checkout.`
        );
        return previous;
      }
      return [...previous, orderId];
    });

  const totalCents = selected.length * reportPriceCents;

  const checkout = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/report-checkout", { orderIds: selected });
      if (data.demo) {
        toast.success(data.message || "Demo payment completed.");
        setSelected([]);
        onCheckedOut?.();
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Report checkout failed."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="report-cart-panel">
      <CardHeader className="pb-3">
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" /> Report checkout
        </CardTitle>
        <CardDescription data-testid="cart-limit-description">
          {tier
            ? `Your ${tier.name} membership allows up to ${maxPerCheckout} reports per checkout at ${centsToUsd(reportPriceCents)} each.`
            : `Select verified requests at ${centsToUsd(reportPriceCents)} per report.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {eligible.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="cart-empty-state">
            No eligible requests yet. After extraction completes, review &amp; confirm the extracted
            information on each request — confirmed unpaid requests appear here.
          </p>
        ) : (
          <ScrollArea className="max-h-56">
            <ul className="space-y-2 pr-2">
              {eligible.map((order) => (
                <li
                  key={order.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3"
                >
                  <Checkbox
                    id={`cart-${order.id}`}
                    checked={selected.includes(order.id)}
                    onCheckedChange={() => toggle(order.id)}
                    data-testid={`cart-select-${order.id}`}
                  />
                  <label htmlFor={`cart-${order.id}`} className="flex-1 cursor-pointer min-w-0">
                    <span className="block text-sm font-medium capitalize">
                      {order.request_type.replaceAll("_", " ")} &middot;{" "}
                      <span className="font-mono text-xs">{order.identifier_value}</span>
                    </span>
                    <span className="block text-xs text-muted-foreground font-mono">{order.id.slice(0, 8)}</span>
                  </label>
                  <span className="text-sm font-mono text-muted-foreground">{centsToUsd(reportPriceCents)}</span>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}

        <Separator />
        <p
          className="text-[11px] text-muted-foreground leading-relaxed rounded-md border border-border bg-secondary/30 p-2.5"
          data-testid="cart-processing-disclosure"
        >
          <span className="font-medium text-foreground">Processing time:</span> reports are usually fulfilled
          within 14&ndash;30 days after entering the administrative queue. Depending on volume, processing can
          take up to 30&ndash;60 days per report.
        </p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selected.length} of {maxPerCheckout} report{maxPerCheckout === 1 ? "" : "s"} selected
          </span>
          <span className="font-display text-xl font-semibold" data-testid="cart-total">
            {centsToUsd(totalCents)}
          </span>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              className="w-full"
              disabled={selected.length === 0 || submitting}
              data-testid="cart-checkout-button"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                `Checkout ${selected.length || ""} report${selected.length === 1 ? "" : "s"}${demoMode ? " (Demo)" : ""}`
              )}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-popover border-border" data-testid="cart-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-display">Confirm report checkout</AlertDialogTitle>
              <AlertDialogDescription>
                You are purchasing {selected.length} report request{selected.length === 1 ? "" : "s"} for a total of{" "}
                <span className="font-mono text-foreground">{centsToUsd(totalCents)}</span>. Reports are usually
                fulfilled within 14&ndash;30 days after entering the administrative queue; depending on volume,
                processing can take up to 30&ndash;60 days per report.
                {demoMode
                  ? " Demo mode is active — payment will be simulated and the requests will be marked paid immediately."
                  : " You will be redirected to Stripe to complete payment. Applicable tax is calculated at checkout."}
              </AlertDialogDescription>
              <AlertDialogDescription
                className="text-[11px] leading-relaxed border-t border-border pt-3"
                data-testid="cart-consent-copy"
              >
                By continuing, you agree to the Terms of Service, Membership Agreement, Refund &amp; Cancellation
                Policy, and Privacy Policy, and you acknowledge that reports are informational only, are not legal
                or financial advice, do not guarantee any outcome, and that GetYourQSIP does not issue official
                CUSIP&reg; identifiers and is not affiliated with CUSIP Global Services.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cart-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={checkout} data-testid="cart-confirm-proceed">
                {demoMode ? "Pay (Demo)" : "Continue to Stripe"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
